import { useCallback, useContext, useRef } from 'react';

import { healthCheck } from 'PrivateApis';

import { UserContext } from 'Context/user';

import { useRefreshIdToken } from './useRefreshIdToken';

const getHealthCheck = async () => healthCheck()
  .catch((err: Error) => {
    window?.Sentry?.captureException?.(new Error('Health check endpoint errors'), {
      extra: {
        tag: 'refresh cognito id token',
      },
      level: 'info',
    });

    return ({ error: err });
  });

export const useSetRefreshToken = () => {
  const { userState } = useContext(UserContext);

  const { attemptTokenRefresh } = useRefreshIdToken();

  const sessionInterval = useRef<ReturnType<typeof setInterval>>();
  const isOnline = useRef<boolean>(globalThis?.navigator?.onLine);

  const refreshTokenInterval = (((userState?.cognitoTokens?.expiresIn ?? 0) - 60) * 1000);

  const attemptHealthCheckAndTokenRefresh = useCallback(async () => {
    const healthResponse = await getHealthCheck();
    if (healthResponse?.error) {
      isOnline.current = false;
    } else {
      await attemptTokenRefresh();
    }
  }, [attemptTokenRefresh]);

  const cleanupRefreshInterval = useCallback(() => {
    isOnline.current = false;
    clearInterval(sessionInterval.current);
    sessionInterval.current = undefined;
  }, []);

  const setCognitoIdTokenRefreshInterval = useCallback(async (triggerOnLoad = true) => {
    clearInterval(sessionInterval.current);
    sessionInterval.current = undefined;

    if (triggerOnLoad) {
      await attemptHealthCheckAndTokenRefresh();
    }

    sessionInterval.current = setInterval(async () => {
      await attemptHealthCheckAndTokenRefresh();
    }, refreshTokenInterval);
  }, [attemptHealthCheckAndTokenRefresh, refreshTokenInterval]);

  const triggerRefreshInterval = useCallback(async () => {
    isOnline.current = true;
    await setCognitoIdTokenRefreshInterval();
  }, [setCognitoIdTokenRefreshInterval]);

  return {
    setCognitoIdTokenRefreshInterval,
    stopRefreshInterval: cleanupRefreshInterval,
    triggerRefreshInterval,
  };
};
